import {
  GetCombinedLayersForSourceData,
  GetSingleLayerForSourceData,
  GetSingleLayerForSourceWithDateData,
  ListAllLayersForSourceResponse,
} from "regen-api-ts-client";

import { FERTILIZER_RECOMMENDATION_DATATYPES } from "../Map/MapLayers/fertilizerRecommendationLayerTypes";
import { NUTRIENT_TESTING_DATATYPES } from "../Map/MapLayers/nutrientTestingLayerTypes";
import { SATELLITE_IMAGERY_DATATYPES } from "../Map/MapLayers/satelliteImageryLayerTypes";
import { SSURGO_DATATYPES } from "../Map/MapLayers/ssurgoTypes";

export const NO_DATA_COLOR = "#dadaeb";

// Datasource IDs categorized by response types
export type SingleLayerDatasourceId =
  GetSingleLayerForSourceData["path"]["source"];
export type CombinedLayerDatesourceId =
  GetCombinedLayersForSourceData["path"]["source"];
export type SingleLayerWithDateDatasourceId =
  GetSingleLayerForSourceWithDateData["path"]["source"];

export type DatasourceId =
  | SingleLayerDatasourceId
  | CombinedLayerDatesourceId
  | SingleLayerWithDateDatasourceId
  | "field_boundaries";

export type MapDropdownOption<T> = {
  id: T;
  name: string;
  defaultLayer?: MapDropdownOption<LayerId>;
  layerOptions?: MapDropdownOption<LayerId>[];
};

// Datasource config
export const DATASOURCES: {
  [key in DatasourceId]: MapDropdownOption<DatasourceId>;
} = {
  field_boundaries: {
    id: "field_boundaries",
    name: "Field boundaries",
  },
  ssurgo: {
    id: "ssurgo",
    name: "Field properties",
    defaultLayer: {
      id: "awc",
      name: "Available Water Capacity",
    },
    layerOptions: SSURGO_DATATYPES,
  },
  nutrient_testing: {
    id: "nutrient_testing",
    name: "Nutrient test results",
    defaultLayer: {
      id: "vendor_cec_meq_100_g",
      name: "CEC",
    },
    layerOptions: NUTRIENT_TESTING_DATATYPES,
  },
  fertilizer_recommendation: {
    id: "fertilizer_recommendation",
    name: "Fertilizer recommendations",
    defaultLayer: {
      id: "rx_lime",
      name: "Lime",
    },
    layerOptions: FERTILIZER_RECOMMENDATION_DATATYPES,
  },
  satellite_imagery: {
    id: "satellite_imagery",
    name: "Satellite Imagery",
    defaultLayer: {
      id: "ndvi",
      name: "NDVI",
    },
    layerOptions: SATELLITE_IMAGERY_DATATYPES,
  },
};

export type LayerId = ListAllLayersForSourceResponse[0];

// List of all possible layer id values. This is structured as an object so that the keys
// contain every LayerId value. If more LayerId values are added to any datasource, this
// object will need to be updated.
const LAYERS: { [key in LayerId]: boolean } = {
  awc: true,
  cec7: true,
  clay_total: true,
  drainage_class: true,
  farmland_class: true,
  flood_frequency_dcd: true,
  soil_erodability_factor: true,
  organic_matter: true,
  sand_total: true,
  silt_total: true,
  taxonomic_class: true,
  wind_erodability: true,
  water_table_depth_annual_min: true,
  water_table_depth_q2_min: true,
  vendor_cec_meq_100_g: true,
  vendor_ph_h20: true,
  vendor_ph_buffer: true,
  vender_om: true,
  vendor_p_ppm: true,
  vendor_k_ppm: true,
  vendor_ca_ppm: true,
  vendor_mg_ppm: true,
  vendor_s_ppm: true,
  vendor_na_ppm: true,
  vendor_zn_ppm: true,
  vendor_fe_ppm: true,
  vendor_mn_ppm: true,
  vendor_cu_ppm: true,
  vendor_b_ppm: true,
  vendor_ca_sat_pct: true,
  vendor_mg_sat_pct: true,
  vendor_k_sat_pct: true,
  base_sat: true,
  vendor_na_sat_pct: true,
  vendor_h_sat_pct: true,
  rx_lime: true,
  rx_s: true,
  rx_p2o5_general: true,
  rx_k2o_general: true,
  ndvi: true,
  baseline_ndvi: true,
  evi: true,
  msavi: true,
  ndre: true,
  ccci: true,
  ndvi_anomaly: true,
};

// Check whether a given string is a valid layer
export const isLayerType = (key: string): key is LayerId =>
  Object.keys(LAYERS).includes(key as LayerId);

// Layer ids for datasources which return a single layer of data
export type SingleLayerId = GetSingleLayerForSourceData["path"]["layer"];

export const SINGLE_LAYERS: SingleLayerId[] = [
  "awc",
  "cec7",
  "clay_total",
  "drainage_class",
  "farmland_class",
  "flood_frequency_dcd",
  "soil_erodability_factor",
  "organic_matter",
  "sand_total",
  "silt_total",
  "taxonomic_class",
  "wind_erodability",
  "water_table_depth_annual_min",
  "water_table_depth_q2_min",
];

export const isSingleLayerType = (key: string): key is SingleLayerId =>
  SINGLE_LAYERS.includes(key as SingleLayerId);

// Layer ids for datasources which return a single layer of data for a given date
export type SingleLayerWithDateLayerId =
  GetSingleLayerForSourceWithDateData["path"]["layer"];

export const SINGLE_LAYER_WITH_DATE_LAYERS: SingleLayerWithDateLayerId[] = [
  "ndvi",
  "baseline_ndvi",
  "evi",
  "msavi",
  "ndre",
  "ccci",
  "ndvi_anomaly",
];

export const isSingleLayerWithDateType = (
  key: string,
): key is SingleLayerWithDateLayerId =>
  SINGLE_LAYER_WITH_DATE_LAYERS.includes(key as SingleLayerWithDateLayerId);

// Default datasource displayed on the interactive map.
export const DEFAULT_DATASOURCE = DATASOURCES.nutrient_testing;

// Used to capture map link info in module section configs.
export type MapInfo = {
  datasource: DatasourceId;
  layer?: LayerId;
};

// Map display mode set in the URL.
export enum MapModes {
  DEFAULT = "default",
  COMPARE = "compare",
}

// Mapbox style
export enum MapStyle {
  SatelliteStreets = "mapbox://styles/mapbox/satellite-streets-v12",
  Streets = "mapbox://styles/mapbox/streets-v12",
}

// List of all possible layers across the different datasources.
export const DATA_LAYERS = [
  ...NUTRIENT_TESTING_DATATYPES,
  ...FERTILIZER_RECOMMENDATION_DATATYPES,
  ...SSURGO_DATATYPES,
  ...SATELLITE_IMAGERY_DATATYPES,
];

export const getDataLayerById = (layerId?: LayerId) =>
  DATA_LAYERS.find((layer) => layer.id == layerId);
